import { IconBrandGoogleFilled } from "@tabler/icons-react";

import type { AuthConfig } from "../api/types";
import { SUPPORT_CHANNEL } from "../config";
import { GateCard } from "./GateCard";
import { signInErrorMessage } from "./signInErrors";

/**
 * Build the sign-in link, asking to come back where the person was.
 *
 * Args:
 *     signInUrl: The URL the server published for starting a sign-in.
 *     next: Path to return to once the session exists.
 *
 * Returns:
 *     The link to put on the button.
 */
export function signInHref(signInUrl: string, next: string): string {
  const separator = signInUrl.includes("?") ? "&" : "?";
  return `${signInUrl}${separator}next=${encodeURIComponent(next)}`;
}

interface SignInScreenProps {
  config: AuthConfig;
  /** Where to return after signing in. */
  next: string;
  /** The `error` code the server redirected with, if any. */
  errorCode?: string | null;
}

/**
 * The only way in: one button, and the domain it accepts.
 *
 * The browser never handles a token. The button is a plain link to the server,
 * which redirects to Google, checks what comes back and sets a cookie this
 * page cannot read (`docs/AUTH.md` §1). There is nothing to type here, and
 * nothing to choose.
 *
 * Args:
 *     config: What the server published about how it signs people in.
 *     next: Path to return to once signed in.
 *     errorCode: Failure code from a sign-in that just came back.
 *
 * Returns:
 *     The sign-in screen.
 */
export function SignInScreen({
  config,
  next,
  errorCode = null,
}: SignInScreenProps): JSX.Element {
  const problem = signInErrorMessage(errorCode ?? null, config.domain);

  if (!config.sign_in_url) {
    // Development mode: nobody is signed in and nobody can be. Saying "sign in
    // with Google" here would offer a door that does not exist.
    return (
      <GateCard title="The console did not accept this browser">
        {problem ? (
          <p className="gate__error" role="alert">
            {problem}
          </p>
        ) : null}
        <p className="gate__text">
          This console runs in development mode, so it signs nobody in — and it
          still refused this page. Restart it with Google sign-in configured, or
          tell {SUPPORT_CHANNEL}.
        </p>
        <button
          type="button"
          className="btn"
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </GateCard>
    );
  }

  return (
    <GateCard title="Sign in to Data Console">
      {problem ? (
        <p className="gate__error" role="alert">
          {problem}
        </p>
      ) : null}
      <a
        className="btn btn--primary btn--block"
        href={signInHref(config.sign_in_url, next)}
      >
        <IconBrandGoogleFilled size={15} aria-hidden="true" />
        Sign in with Google
      </a>
      <p className="gate__text">
        {config.domain
          ? `Neuralk accounts only — sign in with your @${config.domain} Google account.`
          : "Neuralk accounts only."}
      </p>
    </GateCard>
  );
}
